import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Accordion, AccordionBody, AccordionHeader, AccordionList } from '@components/accordion';
import { Divider } from '@components/divider';
import { Error } from '@components/error/src/error';
import { ImportCSVForm } from '@components/process-import-csv/src/process-import-csv.type';
import { Select, SelectOption } from '@components/select';
import classNames from 'classnames';

import { bookingFields } from '../utils/validate-import-csv';

export const CsvColumns = ({
  processedColumns,
  isMobileViewPort,
  removeFirstRow,
}: {
  processedColumns: Array<string[]>;
  isMobileViewPort: boolean;
  removeFirstRow: boolean;
}) => {
  const { t } = useTranslation();
  const { control, formState, watch } = useFormContext<ImportCSVForm & { columns: { field: string }[] }>();
  const watchedColumns = watch('columns');
  const COLUMN_LENGHT_VALUE = 4;
  return (
    <AccordionList className='flex flex-col gap-3 sm:grid sm:grid-cols-3'>
      {processedColumns.map((column: unknown[], colIndex: number) => (
        <div key={`column-wrapper-${colIndex}`} className='min-w-0'>
          <Accordion
            key={`column-item-${colIndex}`}
            className='border-0 bg-transparent dark:bg-transparent'
            defaultOpen={!isMobileViewPort}>
            <AccordionHeader
              className={classNames('border-0 text-headline-xs', {
                'pointer-events-none': !isMobileViewPort,
              })}
              showChevron={isMobileViewPort}>
              {t('common:columnValue', { columnValue: colIndex + 1 })}
            </AccordionHeader>
            <AccordionBody className='px-0'>
              <div className='flex flex-col w-full'>
                <div className='pb-10 overflow-hidden'>
                  <div className='relative'>
                    <Controller
                      name={`columns.${colIndex}.field`}
                      control={control}
                      render={({ field }) => (
                        <Select
                          {...field}
                          selectFontLight
                          value={field.value}
                          onChange={(e) => field.onChange(e)}
                          hasError={!!formState.errors.columns?.[colIndex]?.field}>
                          <SelectOption value='' label={t('common:skipThisColumnPlaceholder')} />
                          {bookingFields.map((fieldOption) => (
                            <SelectOption
                              key={fieldOption.id}
                              value={fieldOption.id}
                              label={t(`${fieldOption.id}ImportField`)}
                            />
                          ))}
                        </Select>
                      )}
                    />
                    {formState.errors.columns?.[colIndex]?.field && (
                      <Error className='absolute inset-x-0 top-full mt-1 wrap-break-word line-clamp-2'>
                        {t(`${bookingFields.find((field) => field.id === watchedColumns[colIndex]?.field)?.error}`)}
                      </Error>
                    )}
                  </div>
                </div>
                <div className='flex flex-col gap-3 bg-surface-secondary p-4 rounded-md'>
                  {column.slice(0, COLUMN_LENGHT_VALUE).map((item: unknown, rowIndex: number) => (
                    <p
                      key={`row-${colIndex}-${rowIndex}`}
                      className={classNames('text-left text-primary', {
                        'text-subtitle-s mb-1': rowIndex === 0,
                        'text-body-s': rowIndex !== 0,
                        'line-through': removeFirstRow && rowIndex === 0,
                      })}>
                      {item === '' ? '-' : (item as string)}
                    </p>
                  ))}
                  {column.length > COLUMN_LENGHT_VALUE && (
                    <p className='text-left text-primary'>
                      +{column.length - COLUMN_LENGHT_VALUE} {t('common:moreRows')}
                    </p>
                  )}
                </div>
              </div>
            </AccordionBody>
          </Accordion>
          {isMobileViewPort && <Divider className='my-2' />}
        </div>
      ))}
    </AccordionList>
  );
};
